import React, {PureComponent} from 'react';
import i18n from '../../../utils/i18n';
import PropTypes from 'prop-types';
import cx from 'classnames';
import lodashNoop from 'lodash/noop';
import deleteIcon from '@adsk/bim360-matrix-react-assets/trash.svg';
import editIcon from '@adsk/bim360-matrix-react-assets/edit-icon.svg';
import lockIcon from '@adsk/bim360-matrix-react-assets/lock.svg';
import Button from '@adsk/bim360-matrix-react-button';
import MatrixTable from '@adsk/bim360-matrix-react-matrix-table';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import Toggle from '@adsk/bim360-matrix-react-toggle';
import {canDeleteIssueAttribute, canEditIssueAttribute, shouldShowValues} from '../../../utils/issueAttributes-helpers';
import IssueAttributesAdminTypeIconAndName from './IssueAttributesAdminTypeIconAndName';
import IssueAttributesAdminCreateButton from './IssueAttributesAdminCreateButton';
import {
  ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES,
  ISSUE_ATTRIBUTE_MAPPING_FIELD_NAMES
} from '../../../constants/issue-attributes';
import IssueAttributesScrollableTooltipCell from './IssueAttributesScrollableTooltipCell';
import IssueAttributesAdminTableMappingsCell from './IssueAttributesAdminTableMappingsCell';
import {hasPermittedAttribute} from '../../../utils/issues-helpers';

class IssueAttributesAdminTable extends PureComponent {
  constructor(props) {
    super(props);

    this.setRowClassName = this.setRowClassName.bind(this);
    this.getActionOptions = this.getActionOptions.bind(this);
    this.handleRowAction = this.handleRowAction.bind(this);
    this.onRowClick = this.onRowClick.bind(this);
    this._renderTitle = this._renderTitle.bind(this);
    this._renderType = this._renderType.bind(this);
    this._renderValues = this._renderValues.bind(this);
    this._renderIsRequiredCell = this._renderIsRequiredCell.bind(this);
    this._renderMappedIssueTypesCell = this._renderMappedIssueTypesCell.bind(this);
    this._inheritanceDataGetter = this._inheritanceDataGetter.bind(this);

    this.columnDefinitions = this.getColumnDefinitions();
    this.columns = this.getColumns();
    this.tableActionOptions = {
      EDIT: 'edit',
      DELETE: 'delete',
      REMOVE_MAPPING: 'remove-mapping'
    };

    this.footerHeight = 50;
  }

  getActionOptions({rowData}) {
    const canEdit = canEditIssueAttribute(rowData);
    const canDelete = canDeleteIssueAttribute(rowData);

    const actions = [];
    const svgSize = '16px';

    if (this.props.editModalMode && !this.isInheritedAttribute(rowData)) {
      actions.push({
        key: this.tableActionOptions.REMOVE_MAPPING,
        label: i18n.t('issueAttributesAdmin.actions.remove'),
        svg: deleteIcon,
        width: svgSize,
        height: svgSize
      });
    } else if (!this.props.editModalMode) {
      canEdit &&
        actions.push({
          key: this.tableActionOptions.EDIT,
          label: i18n.t('issueAttributesAdmin.actions.edit'),
          svg: editIcon,
          width: svgSize,
          height: svgSize
        });
      canDelete &&
        actions.push({
          key: this.tableActionOptions.DELETE,
          label: i18n.t('issueAttributesAdmin.actions.delete'),
          svg: deleteIcon,
          width: svgSize,
          height: svgSize
        });
    }

    return actions;
  }

  handleRowAction({key, rowData: issueAttribute}) {
    // eslint-disable-next-line default-case
    switch (key) {
      case this.tableActionOptions.EDIT:
        this.editIssueAttribute(issueAttribute);
        break;
      case this.tableActionOptions.DELETE:
        this.deleteIssueAttribute(issueAttribute);
        break;
      case this.tableActionOptions.REMOVE_MAPPING:
        this.props.removeIssueAttributeMapping(issueAttribute);
        break;
    }
  }

  getColumns() {
    const columns = [
      <MatrixTable.Column key="title" {...this.columnDefinitions.TITLE} />,
      <MatrixTable.Column key="type" {...this.columnDefinitions.TYPE} />,
      <MatrixTable.Column key="values" {...this.columnDefinitions.VALUES} />
    ];

    if (this.props.editModalMode) {
      columns.push(<MatrixTable.Column key="isRequired" {...this.columnDefinitions.IS_REQUIRED} />);
      columns.push(<MatrixTable.Column key="inheritance" {...this.columnDefinitions.INHERITANCE} />);
    } else {
      if (this.props.mapAttributesFromCustomAttributesEnabled) {
        columns.push(<MatrixTable.Column key="mappedIssueTypes" {...this.columnDefinitions.MAPPED_ISSUE_TYPES} />);
      }
      columns.push(<MatrixTable.Column key="description" {...this.columnDefinitions.DESCRIPTION} />);
    }

    return columns;
  }

  getColumnDefinitions() {
    return {
      TITLE: {
        dataKey: 'title',
        title: this.props.editModalMode
          ? i18n.t('issueAttributesAdmin.table.titleShort')
          : i18n.t('issueAttributesAdmin.table.title'),
        width: 200,
        resizable: true,
        dataGetter: this._renderTitle,
        tooltip: ({cellData}) => cellData,
        tooltipProps: {autoTipOnEllipsis: true}
      },
      TYPE: {
        dataKey: 'dataType',
        title: i18n.t('issueAttributesAdmin.table.type'),
        width: 200,
        resizable: true,
        cellRenderer: this._renderType
      },
      VALUES: {
        dataKey: 'metadata',
        title: i18n.t('issueAttributesAdmin.table.values'),
        width: 165,
        resizable: true,
        cellRenderer: this._renderValues
      },
      IS_REQUIRED: {
        title: i18n.t('issueAttributesAdmin.table.isRequired'),
        width: 165,
        resizable: true,
        cellRenderer: this._renderIsRequiredCell
      },
      MAPPED_ISSUE_TYPES: {
        title: i18n.t('issueAttributesAdmin.table.mappedIssueTypes'),
        width: 165,
        resizable: true,
        cellRenderer: this._renderMappedIssueTypesCell
      },
      DESCRIPTION: {
        dataKey: 'description',
        title: i18n.t('issueAttributesAdmin.table.description'),
        width: 300,
        resizable: true,
        tooltipPopoverProps: {
          tooltipClassName: 'IssueAttributesAdminTable__description-tooltip'
        },
        className: 'IssueAttributesAdminTable__description-cell'
      },
      INHERITANCE: {
        dataKey: 'isInherited',
        title: '',
        width: 120,
        className: 'IssueAttributesAdminTable__inheritance-cell',
        dataGetter: this._inheritanceDataGetter
      },
      ACTION: {
        key: 'action',
        title: '',
        width: 50,
        resizable: false,
        className: 'IssueAttributesAdminTable__action-cell',
        style: {
          paddingRight: '20px'
        }
      }
    };
  }

  deleteIssueAttribute(issueAttribute) {
    const {issueAttributeMappings = []} = this.props;
    const {id: definitionId} = issueAttribute;
    const isMapped = issueAttributeMappings.some(mapping => mapping.attributeDefinitionId === definitionId);

    this.props.deleteIssueAttribute(issueAttribute, isMapped);
  }

  editIssueAttribute(issueAttribute) {
    this.props.editIssueAttribute(issueAttribute.id);
  }

  isInheritedAttribute(issueAttribute) {
    return this.props.inheritedIssueAttributes.includes(issueAttribute.id);
  }

  shouldRenderFooter() {
    return this.props.editModalMode;
  }

  setRowClassName({rowData}) {
    return cx('IssueAttributesAdminTable__row', {
      'IssueAttributesAdminTable__row--highlighted': this.props.highlightRowIds.includes(rowData.id)
    });
  }

  onRowClick({rowData: issueAttribute}) {
    this.editIssueAttribute(issueAttribute);
  }

  _renderAttributeBtn() {
    return (
      <Button styleType={Button.StyleType.INLINE_LINK} data-testid="IssueAttributesAdminTable__add-attribute-btn">
        {i18n.t('issueAttributesAdmin.modal.addAttribute')}
      </Button>
    );
  }

  _renderNewAttributeRow() {
    if (!this.shouldRenderFooter()) {
      return null;
    }

    return (
      <div
        className="IssueAttributesAdminTable__add-attribute"
        onClick={this.props.openCreateAttributeModal}
        style={{height: this.footerHeight}}
      >
        {this._renderAttributeBtn()}
      </div>
    );
  }

  _renderTitle({rowData}) {
    const {title} = rowData;
    return (
      <span>
        <span>{title}</span>
        {this.isInheritedAttribute(rowData) && (
          <SvgIcon width="16px" height="14px" className="IssueAttributesAdminTable__lock">
            {lockIcon}
          </SvgIcon>
        )}
      </span>
    );
  }

  _renderType({cellData: dataType, container}) {
    return (
      <IssueAttributesAdminTypeIconAndName
        dataType={dataType}
        svgIconDataTestId="IssueAttributesAdminTable__type-icon"
        titleDataTestId="IssueAttributesAdminTable__type-name"
        container={container}
      />
    );
  }

  _inheritanceDataGetter({rowData: issueAttribute}) {
    return this.isInheritedAttribute(issueAttribute) ? i18n.t('issueAttributesAdmin.modal.inherited') : '';
  }

  _renderValues({rowData: issueAttribute, container}) {
    const {metadata, dataType} = issueAttribute;

    const options = shouldShowValues(issueAttribute) ? metadata[dataType].options : [];
    const optionValues = options.map(option => option.value);
    return <IssueAttributesScrollableTooltipCell options={optionValues} container={container} />;
  }

  _renderIsRequiredCell({rowData: issueAttribute}) {
    const mapping = this.props.issueTypeAttributeMappings.find(map => map.attributeDefinitionId === issueAttribute.id);
    const isInherited = this.isInheritedAttribute(issueAttribute);
    const isNew = !mapping.id;
    // Changing required state is disabled for inherited attributes (or if user has no permissions to do so)
    const enabled =
      !isInherited && (isNew || hasPermittedAttribute(mapping, ISSUE_ATTRIBUTE_MAPPING_FIELD_NAMES.IS_REQUIRED));
    return (
      <Toggle
        defaultChecked={mapping.isRequired}
        onChange={isRequired => this.props.onMappingRequiredChange(mapping, isRequired)}
        disabled={!enabled}
      />
    );
  }

  _renderMappedIssueTypesCell({rowData, container}) {
    const {issueAttributeMappings, fieldIssueTypeEntitiesMap} = this.props;
    const attributeId = rowData.id;
    const mappings = issueAttributeMappings.filter(mapping => mapping.attributeDefinitionId === attributeId);
    if (!mappings.length) {
      return '-';
    }
    const isContainerType = mappings.some(
      mapping => mapping.mappedItemType === ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES.CONTAINER
    );
    if (isContainerType) {
      return i18n.t('issueAttributesAdmin.mappings.allTypes');
    } else {
      return (
        <IssueAttributesAdminTableMappingsCell
          mappings={mappings}
          fieldIssueTypeEntitiesMap={fieldIssueTypeEntitiesMap}
          container={container}
        />
      );
    }
  }

  _renderCreateButton() {
    return (
      <IssueAttributesAdminCreateButton
        data-testid="IssueAttributesAdminTable__empty-state-create-button"
        onClick={this.props.openCreateModal}
      />
    );
  }

  _renderNoIssueAttributesEmptyState() {
    return (
      <EmptyState
        icon={EmptyState.Icons.EMPTY_BOX}
        className="IssueAttributesAdminTable__empty-state"
        text={i18n.t('issueAttributesAdmin.emptyState.noIssueAttributes')}
        footer={this._renderCreateButton()}
      />
    );
  }

  _renderEditModalEmptyState() {
    return (
      <div className="IssueAttributesAdminTable__edit-modal-emptystate">
        <EmptyState
          icon={EmptyState.Icons.EMPTY_BOX}
          className="IssueAttributesAdminTable__empty-state"
          text={i18n.t('issueAttributesAdmin.emptyState.noCustomAttributes')}
        />
        {this._renderNewAttributeRow()}
      </div>
    );
  }

  render() {
    const {tableData, editModalMode} = this.props;

    if (tableData.length === 0) {
      return editModalMode ? this._renderEditModalEmptyState() : this._renderNoIssueAttributesEmptyState();
    }

    const tableClass = cx('IssueAttributesAdminTable', {
      'IssueAttributesAdminTable--edit': editModalMode
    });

    const footerHeight = this.shouldRenderFooter() ? this.footerHeight : 0;

    return (
      <MatrixTable
        attachTestId
        className={tableClass}
        data={tableData}
        maxHeight={Number.MAX_VALUE}
        actionColumnProps={this.columnDefinitions.ACTION}
        actionOptions={this.getActionOptions}
        rowClassName={this.setRowClassName}
        onRowAction={this.handleRowAction}
        actionMode={MatrixTable.ActionMode.COLUMN}
        onRowClick={this.onRowClick}
        footerHeight={footerHeight}
        footerRenderer={this._renderNewAttributeRow()}
      >
        {this.columns}
      </MatrixTable>
    );
  }
}

IssueAttributesAdminTable.displayName = 'IssueAttributesAdminTable';

IssueAttributesAdminTable.defaultProps = {
  highlightRowIds: [],
  editIssueAttribute: lodashNoop,
  openCreateModal: lodashNoop,
  editModalMode: false,
  editModalMappedItemType: ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES.CONTAINER,
  inheritedIssueAttributes: []
};

IssueAttributesAdminTable.propTypes = {
  tableData: PropTypes.arrayOf(PropTypes.object).isRequired,
  openCreateModal: PropTypes.func,
  highlightRowIds: PropTypes.arrayOf(PropTypes.string),
  editIssueAttribute: PropTypes.func,
  deleteIssueAttribute: PropTypes.func,
  openCreateAttributeModal: PropTypes.func,
  editModalMode: PropTypes.bool,
  editModalMappedItemType: PropTypes.oneOf(Object.values(ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES)),
  issueAttributeMappings: PropTypes.arrayOf(PropTypes.object),
  issueTypeAttributeMappings: PropTypes.arrayOf(PropTypes.object),
  inheritedIssueAttributes: PropTypes.arrayOf(PropTypes.string),
  removeIssueAttributeMapping: PropTypes.func,
  onMappingRequiredChange: PropTypes.func,
  fieldIssueTypeEntitiesMap: PropTypes.object.isRequired,
  mapAttributesFromCustomAttributesEnabled: PropTypes.bool
};

export default IssueAttributesAdminTable;
